/**
 * Block 11 (2026-05-27): ФОП ЗСО API client.
 */
import { api } from './client';
import type { Page } from './catalogs';
import type { FopExpenseCategory } from '@/shared/fop_expense_categories';
import type { CancellableState } from './cancellation';


export interface FopZsoQuarterlyCalculation {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  period_year: number;
  period_quarter: number;
  revenue_for_period: string;
  allowed_expenses_amount: string;
  net_income: string;
  pdfo_rate: string;
  wz_rate: string;
  esv_rate: string;
  pdfo_amount: string;
  wz_amount: string;
  esv_amount: string;
  expenses_breakdown: Record<string, string>;
  expense_account_code: string;
  pdfo_account_code: string;
  wz_account_code: string;
  esv_account_code: string;
  cost_item_id: string | null;
  trigger_source: string;
  // Block 5.3f:
  state?: CancellableState;
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}


export interface FopZsoQuarterlyPreview {
  organization_id: string;
  organization_name: string;
  period_year: number;
  period_quarter: number;
  revenue_for_period: string;
  allowed_expenses_amount: string;
  net_income: string;
  pdfo_rate: string;
  wz_rate: string;
  esv_rate: string;
  pdfo_amount: string;
  wz_amount: string;
  esv_amount: string;
  expenses_breakdown: Record<string, string>;
  min_insurance_per_quarter: string;
}


export interface CalculateFopZsoRequest {
  organization_id: string;
  period_year: number;
  period_quarter: number;
  expense_account_code?: string;
  cost_item_id?: string | null;
  document_date?: string | null;
  comment?: string | null;
}


export interface AccEntryFopExpensePatchBody {
  fop_recognized_expense: boolean;
  fop_expense_category?: FopExpenseCategory | null;
  comment?: string | null;
}


export interface AccEntryFopExpensePatchResult {
  entry_id: string;
  old_recognized: boolean;
  old_category: string | null;
  new_recognized: boolean;
  new_category: string | null;
  content: string | null;
}


const BASE = '/documents/fop-zso-quarterly-calculations';


export const fopZso = {
  list: (params?: {
    organization_id?: string;
    period_year?: number;
    period_quarter?: number;
    posted?: boolean;
    limit?: number;
  }) =>
    api.get<Page<FopZsoQuarterlyCalculation>>(BASE, { params })
      .then((r) => r.data),
  get: (id: string) =>
    api.get<FopZsoQuarterlyCalculation>(`${BASE}/${id}`).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`${BASE}/${id}`).then(() => undefined),
  unpost: (id: string) =>
    api.post<FopZsoQuarterlyCalculation>(`${BASE}/${id}/unpost`)
      .then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`${BASE}/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
  preview: (body: CalculateFopZsoRequest) =>
    api.post<FopZsoQuarterlyPreview>(`${BASE}/preview`, body)
      .then((r) => r.data),
  calculateAndPost: (body: CalculateFopZsoRequest) =>
    api.post<FopZsoQuarterlyCalculation>(`${BASE}/calculate-and-post`, body)
      .then((r) => r.data),
  patchExpenseRecognition: (
    entry_id: string, body: AccEntryFopExpensePatchBody,
  ) =>
    api.patch<AccEntryFopExpensePatchResult>(
      `/acc-entries/${entry_id}/fop-expense-recognition`, body,
    ).then((r) => r.data),
};
